"use client";

import React, { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import useEmblaCarousel from "embla-carousel-react";
import { Car, ChevronRight, ChevronLeft, Copy, Check, Image as ImageIcon } from "lucide-react";
import { fetchOffers } from "@/lib/offers-api";

export interface OfferType {
  id: number;
  title: string;
  description: string;
  image: string;
  code: string;
}

export function OfferCard({
  offer,
  index,
}: {
  offer: OfferType;
  index: number;
}) {
  const [imgError, setImgError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setImgError(false);
    setRetryCount(0);
  }, [offer.image]);

  const handleImgError = () => {
    if (retryCount < 2) {
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
      }, 300);
    } else {
      setImgError(true);
    }
  };

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(offer.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy coupon code:", err);
    }
  };

  const imgSrc = retryCount > 0 ? `${offer.image}?retry=${retryCount}` : offer.image;
  const hasImage = Boolean(offer.image) && !imgError;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1], delay: index * 0.05 }}
      className="flex-[0_0_90%] min-w-0 sm:flex-[0_0_45%] md:flex-[0_0_30%] lg:flex-[0_0_24%]"
    >
      <div className="group rounded-2xl overflow-hidden bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col justify-between">
        <div>
          <div className="relative h-40 overflow-hidden bg-gradient-to-br from-neutral-100 via-neutral-200/60 to-neutral-300/80 dark:from-neutral-800 dark:via-neutral-850 dark:to-neutral-900 border-b border-neutral-200/60 dark:border-neutral-800 flex flex-col items-center justify-center">
            {hasImage ? (
              <img
                src={imgSrc}
                alt={offer.title}
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={handleImgError}
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-4 text-center select-none">
                <div className="h-12 w-12 rounded-xl bg-white/80 dark:bg-neutral-800/80 border border-neutral-200/80 dark:border-neutral-700/80 shadow-2xs flex items-center justify-center text-neutral-400 dark:text-neutral-500 mb-1.5 group-hover:scale-110 transition-transform duration-300">
                  <ImageIcon className="h-6 w-6 stroke-[1.5]" aria-hidden />
                </div>
                <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 tracking-wider uppercase">
                  IK Exclusive Offer
                </span>
              </div>
            )}
          </div>
          <div className="p-5 text-center flex flex-col">
            <h3 className="text-xl font-bold text-[#131222] dark:text-white mb-2">
              {offer.title}
            </h3>
            <div className="min-h-[2.5rem] mb-4">
              {offer.description && (
                <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                  {offer.description}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between w-full h-[52px] px-4 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50/50 dark:bg-neutral-800/30">
            <span className="font-mono font-bold text-base text-[#131222] dark:text-white tracking-wider">
              {offer.code}
            </span>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                copied
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "bg-[#FFB51D] text-[#131222] hover:bg-[#FFB51D]/80 hover:scale-[1.02] cursor-pointer"
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function IKTrendingOffers() {
  const [offers, setOffers] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(true);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: "auto",
  });

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    let active = true;
    fetchOffers("home").then((data) => {
      if (active) {
        setOffers(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onInit);
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
      onInit();
    }
  }, [offers, emblaApi, onInit]);

  if (loading) {
    return (
      <div className="relative py-8 sm:py-10 md:py-12 lg:py-16 container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded w-48 mx-auto animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800 p-5 space-y-4 bg-white dark:bg-neutral-900/50 animate-pulse">
              <div className="h-40 bg-neutral-200 dark:bg-neutral-800 rounded-xl" />
              <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-3/4 mx-auto" />
              <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2 mx-auto" />
              <div className="h-12 bg-neutral-200 dark:bg-neutral-800 rounded-lg w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return null;
  }

  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="trending-offers-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
          className="mb-12 text-center"
        >
          <h2
            id="trending-offers-heading"
            className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white"
          >
            Trending Offers
          </h2>
        </motion.div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6 touch-pan-y">
              {offers.map((offer, index) => (
                <OfferCard key={offer.id} offer={offer} index={index} />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 gap-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
                className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                aria-label="Previous offers"
              >
                <ChevronLeft className="w-6 h-6" aria-hidden />
              </button>
              <button
                type="button"
                onClick={scrollNext}
                disabled={nextBtnDisabled}
                className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#FFB51D] text-[#FFB51D] bg-white shadow-md hover:bg-[#FFB51D] hover:text-[#131222] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FFB51D] transition-colors"
                aria-label="Next offers"
              >
                <ChevronRight className="w-6 h-6" aria-hidden />
              </button>
            </div>

            {scrollSnaps.length > 1 && (
              <div
                className="flex gap-2"
                role="tablist"
                aria-label="Offer carousel dots"
              >
                {scrollSnaps.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => scrollTo(index)}
                    role="tab"
                    aria-selected={index === selectedIndex}
                    aria-label={`Go to offer ${index + 1}`}
                    className="group min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full"
                  >
                    <span
                      className={`block rounded-full transition-all ${
                        index === selectedIndex
                          ? "w-8 h-2 bg-[#FFB51D]"
                          : "w-2 h-2 bg-[#131222]/50 group-hover:bg-[#131222]/70"
                      }`}
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}
